// skeleton\app\src\services\httpService.ts

import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosHeaders,
  AxiosRequestConfig,
} from "axios";
import { jwtDecode } from "jwt-decode";
import { ApiEndpoints } from "../constants/appConstants";

type AuthResponse = { Token?: string; token?: string };

interface DecodedExp {
  exp: number;
}

class HttpService {
  private instance: AxiosInstance;
  private refreshClient: AxiosInstance;
  private refreshPromise: Promise<void> | null = null;

  constructor() {
    this.instance = axios.create({
      baseURL: import.meta.env.VITE_API_URL,
    });

    this.refreshClient = axios.create({
      baseURL: import.meta.env.VITE_API_URL,
    });

    this.instance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        try {
          await this.ensureFreshTokens(config);

          const hdrs = config.headers;
          const headers: AxiosRequestHeaders =
            (hdrs instanceof AxiosHeaders
              ? hdrs.toJSON()
              : hdrs || {}) as AxiosRequestHeaders;
          config.headers = headers;

          const oidcAccessToken = sessionStorage.getItem("access_token");
          const apiJwtToken = sessionStorage.getItem("authorizationToken");

          const appType = this.getAppType();

          const base = (config.baseURL ?? "").toLowerCase();
          const path = (config.url ?? "").toLowerCase();
          const fullUrl = base + path;

          const isValidateOrCreateUser =
            fullUrl.includes("/api/user/validateorcreateuser");

          const isGetAuthorizationToken =
            fullUrl.includes("/api/user/getauthorizationtoken") ||
            (ApiEndpoints.getAuthorizationToken &&
              fullUrl.includes(
                ApiEndpoints.getAuthorizationToken.toLowerCase()
              ));

          const isUserLogout =
            fullUrl.includes("/api/user/logout") ||
            (ApiEndpoints.Logout &&
              fullUrl.includes(ApiEndpoints.Logout.toLowerCase()));

          const isMeasureStandardUpdate =
            fullUrl.includes(
              "/api/performancestandard/updatemeasurestandarddetails"
            ) ||
            (ApiEndpoints.UpdateMeasureStandardDetails &&
              fullUrl.includes(
                ApiEndpoints.UpdateMeasureStandardDetails.toLowerCase()
              ));

          const wantsSiteMinder =
            isValidateOrCreateUser ||
            isGetAuthorizationToken ||
            isUserLogout;

          const bearer = wantsSiteMinder ? oidcAccessToken : apiJwtToken;

          if (bearer) {
            (config.headers as any)["Authorization"] = `Bearer ${bearer}`;
          } else {
            delete (config.headers as any)["Authorization"];
          }

          if (isUserLogout && apiJwtToken) {
            (config.headers as any)["X-Custom-Authorization"] =
              `Bearer ${apiJwtToken}`;
          } else if (isUserLogout) {
            console.warn(
              "[HTTP] Logout call detected but no backend JWT found in sessionStorage"
            );
          }

          if (isMeasureStandardUpdate && apiJwtToken) {
            (config.headers as any)["X-Custom-Authorization"] =
              `Bearer ${apiJwtToken}`;
          }

          (config.headers as any)["AppType"] = appType;

          if (wantsSiteMinder) {
            (config.headers as any)["Authprovider"] = "siteminder";
            (config.headers as any)["X-IdP"] = "siteminder";
          } else {
            delete (config.headers as any)["Authprovider"];
            delete (config.headers as any)["X-IdP"];
          }

          const tokenPreview = bearer
            ? bearer.slice(0, 12) + "…"
            : "(none)";

          console.log(
            "[HTTP] →",
            (config.method || "GET").toUpperCase(),
            config.url,
            "| TokenUsed:",
            wantsSiteMinder ? "SiteMinder(OIDC)" : "BackendJWT",
            "| TokenPreview:",
            tokenPreview,
            "| AppType:",
            appType,
            "| Authprovider:",
            (config.headers as any)["Authprovider"] || "(none)",
            "| IsValidateOrCreateUser:",
            isValidateOrCreateUser,
            "| IsGetAuthorizationToken:",
            isGetAuthorizationToken,
            "| IsLogout:",
            isUserLogout,
            "| IsMeasureStandardUpdate:",
            isMeasureStandardUpdate
          );

          if (isUserLogout || isMeasureStandardUpdate) {
            console.log("[HTTP] Headers snapshot:", {
              Authorization: !!(config.headers as any)["Authorization"],
              XCustomAuthorization:
                !!(config.headers as any)["X-Custom-Authorization"],
            });
          }

          if (!bearer && !isUserLogout) {
            console.warn("[HTTP] No token available", {
              fullUrl,
              wantsSiteMinder,
              hasOidc: !!oidcAccessToken,
              hasApiJwt: !!apiJwtToken,
            });
          }
        } catch (err) {
          console.error("[HTTP Request Interceptor] Error:", err);
        }

        return config;
      },
      (error) => {
        console.error(
          "[HTTP Request Interceptor] Exception:",
          error
        );
        return Promise.reject(error);
      }
    );

    this.instance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        try {
          const status = error.response?.status;

          const raw = error.response?.headers ?? {};
          const hdrs =
            raw instanceof AxiosHeaders
              ? (raw as AxiosHeaders).toJSON()
              : (raw as Record<string, string>);

          const wwwAuth = (
            hdrs["www-authenticate"] ||
            hdrs["WWW-Authenticate"] ||
            null
          ) as string | null;

          const hadAuthHeader =
            !!(error.config?.headers as any)?.["Authorization"];

          const requestUrl = (
            error.config?.url || ""
          ).toLowerCase();

          const isValidateOrCreateUser =
            requestUrl.includes("/user/validateorcreateuser");

          const isGetAuthorizationToken =
            requestUrl.includes("/user/getauthorizationtoken");

          const isLogout =
            requestUrl.includes("/user/logout");

          console.error(
            "[HTTP Response Error]",
            status,
            error.message,
            {
              url: error.config?.url,
              hadAuthHeader,
              wwwAuthenticate: wwwAuth,
              data: error.response?.data,
            }
          );

          if (status === 401 || status === 403) {
            console.warn("[HTTP] 401/403 received.");

            if (
              isValidateOrCreateUser ||
              isGetAuthorizationToken ||
              isLogout
            ) {
              console.warn(
                "[HTTP] Auth endpoint returned 401/403. Clearing SiteMinder session keys only."
              );

              sessionStorage.removeItem("authenticated");
              sessionStorage.removeItem("access_token");
              sessionStorage.removeItem("id_token");
              sessionStorage.removeItem("accessTokenExp");
            } else {
              console.warn(
                "[HTTP] Non-auth API returned 401/403. Preserving OIDC tokens and clearing backend JWT only."
              );

              sessionStorage.removeItem("authorizationToken");
              sessionStorage.removeItem(
                "authorizationTokenExp"
              );
            }
          }
        } catch (err) {
          console.error(
            "[HTTP Response Interceptor] Exception:",
            err
          );
        }

        return Promise.reject(error);
      }
    );
  }

  private getAppType(): string {
    return (
      localStorage.getItem("providerType") ||
      import.meta.env.VITE_PROVIDER_TYPE ||
      "SCO"
    ).toUpperCase();
  }

  private buildSiteminderHeaders(
    accessToken: string,
    appType?: string
  ) {
    return {
      Authorization: `Bearer ${accessToken}`,
      AppType: (appType || this.getAppType()).toUpperCase(),
      Authprovider: "siteminder",
      "X-IdP": "siteminder",
    };
  }

  private hasUsableSiteminderSession(): boolean {
    const authProvider =
      sessionStorage.getItem("authProvider") ||
      import.meta.env.VITE_AUTH_PROVIDER ||
      "";

    const accessToken =
      sessionStorage.getItem("access_token");

    const authenticated =
      sessionStorage.getItem("authenticated");

    const valid =
      String(authProvider).toLowerCase() === "siteminder" &&
      !!accessToken &&
      authenticated === "true";

    if (!valid) {
      console.log(
        "[HTTP] No usable SiteMinder session for refresh.",
        {
          authProvider,
          authenticated,
          hasAccessToken: !!accessToken,
        }
      );
    }

    return valid;
  }

  private getTokenSecondsLeft(
    token: string | null
  ): number | null {
    if (!token) {
      return null;
    }

    try {
      const decoded: DecodedExp = jwtDecode(token);
      const now = Math.floor(Date.now() / 1000);

      return decoded.exp - now;
    } catch (err) {
      console.warn(
        "[HTTP] Failed to decode token while reading secondsLeft",
        err
      );

      return null;
    }
  }

  private shouldSkipRefresh(
    config: InternalAxiosRequestConfig
  ): boolean {
    const base = (config.baseURL ?? "").toLowerCase();
    const path = (config.url ?? "").toLowerCase();
    const fullUrl = base + path;

    return (
      fullUrl.includes(
        "/api/user/validateorcreateuser"
      ) ||
      fullUrl.includes(
        "/api/user/getauthorizationtoken"
      ) ||
      fullUrl.includes("/api/user/logout")
    );
  }

  private async ensureFreshTokens(
    config: InternalAxiosRequestConfig
  ): Promise<void> {
    try {
      const authProvider =
        sessionStorage.getItem("authProvider") ||
        import.meta.env.VITE_AUTH_PROVIDER ||
        "";

      if (
        String(authProvider).toLowerCase() !==
        "siteminder"
      ) {
        return;
      }

      if (this.shouldSkipRefresh(config)) {
        return;
      }

      if (!this.hasUsableSiteminderSession()) {
        return;
      }

      const oidcAccessToken =
        sessionStorage.getItem("access_token");

      if (!oidcAccessToken) {
        console.warn(
          "[HTTP] Missing OIDC access token during ensureFreshTokens."
        );
        return;
      }

      const apiJwtToken =
        sessionStorage.getItem("authorizationToken");

      const oidcSecondsLeft =
        this.getTokenSecondsLeft(oidcAccessToken);

      const apiJwtSecondsLeft =
        this.getTokenSecondsLeft(apiJwtToken);

      const oidcExpired =
        oidcSecondsLeft === null ||
        oidcSecondsLeft <= 0;

      const oidcInsideWarningWindow =
        oidcSecondsLeft !== null &&
        oidcSecondsLeft <= 120;

      const apiJwtMissing = !apiJwtToken;

      const apiJwtNeedsRefresh =
        apiJwtSecondsLeft === null ||
        apiJwtSecondsLeft <= 180;

      console.log("[HTTP] ensureFreshTokens state", {
        oidcSecondsLeft,
        apiJwtSecondsLeft,
        oidcExpired,
        oidcInsideWarningWindow,
        apiJwtMissing,
        apiJwtNeedsRefresh,
      });

      if (oidcExpired) {
        console.warn(
          "[HTTP] OIDC token already expired or unreadable. Skipping interceptor refresh."
        );
        return;
      }

      if (oidcInsideWarningWindow) {
        console.log(
          "[HTTP] OIDC token is inside 2-minute warning window. Skipping interceptor refresh so warning dialog can appear."
        );
        return;
      }

      if (!apiJwtMissing && !apiJwtNeedsRefresh) {
        return;
      }

      console.log(
        "[HTTP] Backend JWT missing or expiring soon while OIDC token is still safely valid. Refreshing backend session only.",
        {
          oidcSecondsLeft,
          apiJwtSecondsLeft,
        }
      );

      if (!this.refreshPromise) {
        this.refreshPromise =
          this.refreshBackendSessionWithToken(
            oidcAccessToken,
            this.getAppType()
          )
            .then(() => undefined)
            .finally(() => {
              this.refreshPromise = null;
            });
      }

      await this.refreshPromise;
    } catch (err) {
      console.error(
        "[HTTP] ensureFreshTokens failed",
        err
      );
    }
  }

  public async validateOrCreateUserWithToken(
    accessToken: string,
    appType?: string
  ): Promise<any> {
    console.log(
      "[HTTP] Direct ValidateOrCreateUser with supplied SiteMinder token"
    );

    const response = await this.refreshClient.post(
      "/User/ValidateOrCreateUser",
      {},
      {
        headers: this.buildSiteminderHeaders(
          accessToken,
          appType
        ),
      }
    );

    return response.data;
  }

  public async getAuthorizationTokenWithToken(
    accessToken: string,
    appType?: string
  ): Promise<string> {
    console.log(
      "[HTTP] Direct GetAuthorizationToken with supplied SiteMinder token"
    );

    try {
      const response =
        await this.refreshClient.get<AuthResponse>(
          "/User/GetAuthorizationToken",
          {
            headers: this.buildSiteminderHeaders(
              accessToken,
              appType
            ),
          }
        );

      const backendToken =
        response.data.Token ?? response.data.token;

      if (!backendToken) {
        console.warn(
          "[HTTP] Direct GetAuthorizationToken response missing token field",
          response.data
        );

        throw new Error(
          "Backend token response missing token field"
        );
      }

      return backendToken;
    } catch (err: any) {
      const status = err?.response?.status;

      const existingBackendToken =
        sessionStorage.getItem("authorizationToken");

      const existingBackendSecondsLeft =
        this.getTokenSecondsLeft(existingBackendToken);

      console.error(
        "[HTTP] Direct GetAuthorizationToken failed",
        {
          status,
          hasExistingBackendToken:
            !!existingBackendToken,
          existingBackendSecondsLeft,
          data: err?.response?.data,
        }
      );

      if (
        status === 400 &&
        existingBackendToken &&
        existingBackendSecondsLeft !== null &&
        existingBackendSecondsLeft > 60
      ) {
        console.warn(
          "[HTTP] Falling back to existing backend authorizationToken after transient 400 from GetAuthorizationToken."
        );

        return existingBackendToken;
      }

      throw err;
    }
  }

  public async refreshBackendSessionWithToken(
    accessToken: string,
    appType?: string
  ): Promise<string> {
    const resolvedAppType = (
      appType || this.getAppType()
    ).toUpperCase();

    console.log(
      "[HTTP] Refreshing backend session with supplied SiteMinder token",
      {
        appType: resolvedAppType,
        tokenPreview:
          accessToken.slice(0, 12) + "…",
      }
    );

    const validateResult =
      await this.validateOrCreateUserWithToken(
        accessToken,
        resolvedAppType
      );

    console.log(
      "[HTTP] Direct ValidateOrCreateUser success",
      validateResult
    );

    const backendToken =
      await this.getAuthorizationTokenWithToken(
        accessToken,
        resolvedAppType
      );

    sessionStorage.setItem(
      "authorizationToken",
      backendToken
    );

    sessionStorage.setItem(
      "authenticated",
      "true"
    );

    try {
      const decodedBackend: DecodedExp =
        jwtDecode(backendToken);

      sessionStorage.setItem(
        "authorizationTokenExp",
        String(decodedBackend.exp)
      );

      console.log(
        "[HTTP] Stored refreshed backend authorizationToken exp:",
        decodedBackend.exp
      );
    } catch (decodeErr) {
      console.warn(
        "[HTTP] Failed to decode refreshed backend authorizationToken",
        decodeErr
      );
    }

    console.log(
      "[HTTP] Stored refreshed backend authorizationToken"
    );

    return backendToken;
  }

  public async get<T>(
    endpoint: string,
    params?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      console.log("[HTTP GET]", endpoint, params);

      return await this.instance.get<T>(endpoint, {
        ...config,
        params: config?.params ?? params,
      });
    } catch (err) {
      console.error(
        "[HTTP GET Error]",
        endpoint,
        err
      );

      throw err;
    }
  }

  public async post<T>(
    endpoint: string,
    data: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      console.log("[HTTP POST]", endpoint, data);

      return await this.instance.post<T>(
        endpoint,
        data,
        config
      );
    } catch (err) {
      console.error(
        "[HTTP POST Error]",
        endpoint,
        err
      );

      throw err;
    }
  }

  public async postBlob(
    endpoint: string,
    data: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<Blob>> {
    try {
      console.log("[HTTP POST Blob]", endpoint);

      return await this.instance.post<Blob>(
        endpoint,
        data,
        {
          ...config,
          responseType: "blob",
        }
      );
    } catch (err) {
      console.error(
        "[HTTP POST Blob Error]",
        endpoint,
        err
      );

      throw err;
    }
  }

  public async put<T>(
    endpoint: string,
    data: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      console.log("[HTTP PUT]", endpoint, data);

      return await this.instance.put<T>(
        endpoint,
        data,
        config
      );
    } catch (err) {
      console.error(
        "[HTTP PUT Error]",
        endpoint,
        err
      );

      throw err;
    }
  }

  public async delete<T>(
    endpoint: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    try {
      console.log("[HTTP DELETE]", endpoint);

      return await this.instance.delete<T>(
        endpoint,
        config
      );
    } catch (err) {
      console.error(
        "[HTTP DELETE Error]",
        endpoint,
        err
      );

      throw err;
    }
  }

  public async getBlob(
    endpoint: string
  ): Promise<AxiosResponse<Blob>> {
    try {
      console.log("[HTTP GET Blob]", endpoint);

      return await this.instance.get(endpoint, {
        responseType: "blob",
      });
    } catch (err) {
      console.error(
        "[HTTP GET Blob Error]",
        endpoint
      );

      throw err;
    }
  }
}

const httpService = new HttpService();

export default httpService;